import {
  filterRowsToSaved,
  hasOwnKey,
  readSavedFilterRows,
  type SavedFilterRow,
} from '~/types/filters';
import {
  isListField,
  operatorsForField,
  type BuyListFilterRow,
} from './buyListFilters';

export const BUY_LIST_FILTER_DATA_KEY = 'buyListFilters';
export const BUY_LIST_TEAM_ACTIVE_KEY = 'teamActive';
export const MY_BUY_LIST_FILTER_DATA_KEY = 'myBuyListFilters';

export type BuyListFilterData = {
  [BUY_LIST_FILTER_DATA_KEY]: SavedFilterRow[];
  [BUY_LIST_TEAM_ACTIVE_KEY]?: boolean;
};

export type MyBuyListFilterData = {
  [MY_BUY_LIST_FILTER_DATA_KEY]: SavedFilterRow[];
};

/**
 * @param teamActive omitted for a named preset, which captures filter rows only — the
 *   team toggle belongs to the unnamed filter the buyer is currently working in.
 */
export function rowsToFilterData(
  rows: BuyListFilterRow[],
  teamActive?: boolean
): BuyListFilterData {
  return {
    [BUY_LIST_FILTER_DATA_KEY]: filterRowsToSaved(rows),
    ...(teamActive === undefined
      ? {}
      : { [BUY_LIST_TEAM_ACTIVE_KEY]: teamActive }),
  };
}

export function myBuyListRowsToFilterData(
  rows: BuyListFilterRow[]
): MyBuyListFilterData {
  return { [MY_BUY_LIST_FILTER_DATA_KEY]: filterRowsToSaved(rows) };
}

function storedFields(filterData: unknown): Record<string, unknown> | null {
  if (typeof filterData !== 'object' || filterData === null) return null;
  return filterData as Record<string, unknown>;
}

/** True for the saved-filter payloads that belong to the organization buy list table. */
export function isBuyListFilterData(filterData: unknown): boolean {
  return hasOwnKey(filterData, BUY_LIST_FILTER_DATA_KEY);
}

export function isMyBuyListFilterData(filterData: unknown): boolean {
  return hasOwnKey(filterData, MY_BUY_LIST_FILTER_DATA_KEY);
}

const BUY_LIST_SAVED_ROW_SCHEMA = {
  operatorsForField,
  isListField,
};

export function readBuyListFilterRows(filterData: unknown): BuyListFilterRow[] {
  const fields = storedFields(filterData);
  if (fields === null) return [];
  return readSavedFilterRows(
    fields[BUY_LIST_FILTER_DATA_KEY],
    BUY_LIST_SAVED_ROW_SCHEMA
  );
}

export function readMyBuyListFilterRows(
  filterData: unknown
): BuyListFilterRow[] {
  const fields = storedFields(filterData);
  if (fields === null) return [];
  return readSavedFilterRows(
    fields[MY_BUY_LIST_FILTER_DATA_KEY],
    BUY_LIST_SAVED_ROW_SCHEMA
  );
}

export function readBuyListTeamActive(filterData: unknown): boolean | null {
  const fields = storedFields(filterData);
  if (fields === null) return null;
  const teamActive = fields[BUY_LIST_TEAM_ACTIVE_KEY];
  return typeof teamActive === 'boolean' ? teamActive : null;
}
